import { createFeedComment, createFeedReply, deleteFeedComment, likeFeedItem, shareFeedItem, unlikeFeedItem } from '../feed/asyncThunks';
import { getInfluencerById, getInfluencerFeed, getInfluencers } from './asyncThunks';

const getEntityId = entity => entity?._id || entity?.id || null;

const findInfluencerFeedItem = (state, feedItemId) =>
  state.getInfluencerFeedInfo.data.find(item => getEntityId(item) === feedItemId);

const updateFeedItemStat = (feedItem, statName, delta) => {
  if (!feedItem) {
    return;
  }

  feedItem.stats = feedItem.stats || {};
  feedItem.stats[statName] = Math.max((feedItem.stats[statName] || 0) + delta, 0);
};

export const getInfluencersBuilder = builder => {
  builder.addCase(getInfluencers.pending, (state, { meta }) => {
    const page = meta.arg.page;
    if (page === 1) {
      state.getInfluencersInfo.pending = true;
    } else {
      state.getInfluencersInfo.fetchMorePending = true;
    }
  });
  builder.addCase(getInfluencers.fulfilled, (state, { payload }) => {
    const { data, maxReached, page } = payload;
    if (page === 1) {
      state.getInfluencersInfo.data = data;
      state.getInfluencersInfo.pending = false;
    } else {
      state.getInfluencersInfo.fetchMorePending = false;
      state.getInfluencersInfo.data = [...state.getInfluencersInfo.data, ...data];
    }
    state.getInfluencersInfo.page = page;
    state.getInfluencersInfo.maxReached = maxReached;
  });
  builder.addCase(getInfluencers.rejected, state => {
    state.getInfluencersInfo.pending = false;
    state.getInfluencersInfo.fetchMorePending = false;
  });
};

export const getInfluencerByIdBuilder = builder => {
  builder.addCase(getInfluencerById.pending, state => {
    state.pending.getInfluencerById = true;
  });
  builder.addCase(getInfluencerById.fulfilled, (state, { payload }) => {
    const { influencer } = payload;
    state.influencer = influencer;
    state.pending.getInfluencerById = false;
  });
  builder.addCase(getInfluencerById.rejected, state => {
    state.pending.getInfluencerById = false;
  });
};

export const getInfluencerFeedBuilder = builder => {
  builder.addCase(getInfluencerFeed.pending, (state, { meta }) => {
    if (meta.arg?.cursor) {
      state.getInfluencerFeedInfo.fetchMorePending = true;
    } else {
      state.getInfluencerFeedInfo.pending = true;
      state.getInfluencerFeedInfo.loaded = false;
      state.getInfluencerFeedInfo.influencerId = meta.arg?.influencerId || null;
      state.getInfluencerFeedInfo.data = [];
      state.getInfluencerFeedInfo.nextCursor = null;
      state.getInfluencerFeedInfo.hasMore = true;
    }
  });

  builder.addCase(getInfluencerFeed.fulfilled, (state, { payload, meta }) => {
    if (meta.arg?.cursor) {
      state.getInfluencerFeedInfo.data = [...state.getInfluencerFeedInfo.data, ...payload.data];
    } else {
      state.getInfluencerFeedInfo.data = payload.data;
    }

    state.getInfluencerFeedInfo.pending = false;
    state.getInfluencerFeedInfo.fetchMorePending = false;
    state.getInfluencerFeedInfo.nextCursor = payload.pagination?.nextCursor || null;
    state.getInfluencerFeedInfo.hasMore = Boolean(payload.pagination?.hasMore);
    state.getInfluencerFeedInfo.limit = payload.pagination?.limit || meta.arg?.limit || state.getInfluencerFeedInfo.limit;
    state.getInfluencerFeedInfo.loaded = true;
    state.getInfluencerFeedInfo.influencerId = meta.arg?.influencerId || null;
  });

  builder.addCase(getInfluencerFeed.rejected, (state, { meta }) => {
    state.getInfluencerFeedInfo.pending = false;
    state.getInfluencerFeedInfo.fetchMorePending = false;
    state.getInfluencerFeedInfo.loaded = true;
    state.getInfluencerFeedInfo.influencerId = meta.arg?.influencerId || null;
  });
};

export const influencerFeedActionsBuilder = builder => {
  builder.addCase(likeFeedItem.pending, (state, { meta }) => {
    const feedItem = findInfluencerFeedItem(state, meta.arg.feedItemId);

    if (feedItem) {
      feedItem.liked = true;
      feedItem.likePending = true;
      updateFeedItemStat(feedItem, 'likesCount', 1);
    }
  });

  builder.addCase(likeFeedItem.fulfilled, (state, { payload }) => {
    const feedItem = findInfluencerFeedItem(state, payload.feedItemId);

    if (feedItem) {
      feedItem.liked = true;
      feedItem.likePending = false;
    }
  });

  builder.addCase(likeFeedItem.rejected, (state, { meta }) => {
    const feedItem = findInfluencerFeedItem(state, meta.arg.feedItemId);

    if (feedItem) {
      feedItem.liked = false;
      feedItem.likePending = false;
      updateFeedItemStat(feedItem, 'likesCount', -1);
    }
  });

  builder.addCase(unlikeFeedItem.pending, (state, { meta }) => {
    const feedItem = findInfluencerFeedItem(state, meta.arg.feedItemId);

    if (feedItem) {
      feedItem.liked = false;
      feedItem.likePending = true;
      updateFeedItemStat(feedItem, 'likesCount', -1);
    }
  });

  builder.addCase(unlikeFeedItem.fulfilled, (state, { payload }) => {
    const feedItem = findInfluencerFeedItem(state, payload.feedItemId);

    if (feedItem) {
      feedItem.liked = false;
      feedItem.likePending = false;
    }
  });

  builder.addCase(unlikeFeedItem.rejected, (state, { meta }) => {
    const feedItem = findInfluencerFeedItem(state, meta.arg.feedItemId);

    if (feedItem) {
      feedItem.liked = true;
      feedItem.likePending = false;
      updateFeedItemStat(feedItem, 'likesCount', 1);
    }
  });

  builder.addCase(shareFeedItem.fulfilled, (state, { payload }) => {
    updateFeedItemStat(findInfluencerFeedItem(state, payload.feedItemId), 'sharesCount', 1);
  });

  builder.addCase(createFeedComment.fulfilled, (state, { payload }) => {
    updateFeedItemStat(findInfluencerFeedItem(state, payload.feedItemId), 'commentsCount', 1);
  });

  builder.addCase(createFeedReply.fulfilled, (state, { payload }) => {
    const comment = payload.comment || {};
    const feedItemId = getEntityId(comment.feedItem) || comment.feedItem || payload.feedItemId;

    updateFeedItemStat(findInfluencerFeedItem(state, feedItemId), 'commentsCount', 1);
  });

  builder.addCase(deleteFeedComment.fulfilled, (state, { payload }) => {
    updateFeedItemStat(findInfluencerFeedItem(state, payload.feedItemId), 'commentsCount', -1);
  });
};
